import {GrmMediaPipeObject} from "@/lib/gooroomee/mediapipe/grm-mediapipe/GrmMediaPipeInterface";
import GrmBlurFilter from "@/lib/gooroomee/mediapipe/grm-mediapipe/implements/GrmBlurFilter";
import GrmFaceDetector from "@/lib/gooroomee/mediapipe/face-detection/GrmFaceDetector";

function createOffscreenCanvas(width, height){
    if(globalThis.OffscreenCanvas){
        return new OffscreenCanvas(width, height);
    }
    let cvs = globalThis.document.createElement('canvas');
    Object.assign(cvs, {width, height});
    return cvs;
}

function deviceInfo(){
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    let match = ua.match(/Version\/([\d.]+).*Safari/);
    if(match){
        return {
            isIOS,
            isSafari: true,
            safariVersion: parseFloat(match[1])
        };
    }
    return {
        isIOS,
        isSafari: false, version: null
    };
}

export default class GrmMediaStreamTrackCanvas {
    _options;
    _paused;
    _props = {};
    _eventListeners = [];
    pipes = []

    static isSupported () {
        return !!globalThis.OffscreenCanvas;
    }

    constructor(options) {
        this._options = Object.assign({}, options || {});
    }

    async init () {
        if(this._gl) return;

        const options = this._options;

        let {stream, fps} = options;
        let track;
        if(stream){
            track = stream.getVideoTracks()[0];
        }
        if(fps){
            this._fps = fps;
        }

        const video = options.video || (() => {
            let video = document.createElement('video');
            video.muted = true;
            video.playsInline = true;
            video.srcObject = stream;
            return video;
        })()
        if(video && video.srcObject && !stream && !track){
            stream = video.srcObject;
            track = stream.getVideoTracks()[0];
        }

        try{
            Object.assign(this._props, {deviceInfo: deviceInfo()});
        }catch(e){
            console.error(e);
        }

        const canvas = options.canvas || (() => {
            let canvas = document.createElement('canvas');
            return canvas;
        })();
        // const ctx = canvas.getContext('2d');

        Object.assign(this._props, {
            video, canvas, track, stream
        })

        if(track){
            const {width, height, frameRate, facingMode} = track.getSettings()
            this.resizeCanvas(width, height);
            this._props.fps = fps || frameRate || 30;
            this._props.facingMode = facingMode;
        }else if(video){
            if(video.videoWidth > 0){
                Object.assign(canvas, {
                    width: video.videoWidth,
                    height: video.videoHeight
                });
            }else{
                video.addEventListener('loadedmetadata', e => {
                    Object.assign(canvas, {
                        width: video.videoWidth,
                        height: video.videoHeight
                    });
                }, {once: true});
            }
        }
    }

    isDestroyed () {
        return this.destroyed === true;
    }
    destroy () {
        this.destroyed = true;
        this.pause();

        let track = this.getVideoMediaStreamTrack();
        if(track){
            track.stop();
        }
    }

    getSourceVideo () {
        let {video, stream} = this._props;
        // if(!video && stream){
        //     const { stream } = this._props.stream;
        //     video = this._props.video = document.createElement('video');
        //     video.autoplay = true;
        //     video.muted = true;
        //     video.setAttribute('playsinline', '');
        //     // document.body.append(video);
        //     video.srcObject = stream;
        // }
        return video;
    }

    getRenderCanvas () {
        return this._props.canvas;
    }

    addVideoFrameEventListener (listener, options={}) {
        const list = this._eventListeners;
        let exist = list.find(ls => ls.listener === listener);
        if(exist) return this;
        list.push({
            listener,
            options: {...options}
        });

        if(options.order){
            list.sort((a, b) => {
                if(a.order === undefined){
                    return 2;
                }else if(b.order === undefined){
                    return -2;
                }else if(a.order === undefined && b.order === undefined){
                    return 0;
                }
                return a.order - b.order;
            });
        }else{
            delete options.order;
        }
    }
    removeVideoFrameEventListener (listener) {
        let idx = this._eventListeners.findIndex(ls => ls.listener === listener);
        if(idx > -1){
            this._eventListeners.splice(idx, 1);
        }
        return this;
    }
    async _triggerVideoFrameEvent ({image, canvas}) {
        // event trigger
        const listeners = this._eventListeners;
        if(listeners && listeners.length === 0){
            return;
        }

        const event = {
            type: 'videoFrame',
            image,
            canvas,
        };
        queueMicrotask(() => {
            try{
                let gc;
                listeners.forEach(el => {
                    if(el.removed || el.options.once === true){
                        gc = gc || [];
                        gc.push(el);
                    }
                    if(el.removed) return;

                    el.listener(
                        Object.assign(event, {
                            removeListener: () => {
                                el.removed = true;
                            }
                        })
                    );
                });
                gc && gc.forEach(el => {
                    let idx = listeners.findIndex(el);
                    if(idx === -1) return;
                    listeners.splice(idx, 1);
                });
            }catch(e){
                console.error(e);
            }
        });
    }

    _getRenderContext () {
        let { ctx } = this._props;
        if(!ctx){
            ctx = this._props.ctx = this._props.canvas.getContext('2d');
            this._props.ctx = ctx;
        }
        return ctx;
    }

    async render () {
        let { video } = this._props;
        if (video.readyState < video.HAVE_ENOUGH_DATA) {
            return;
        }

        const videoFrameRatio = video.videoWidth / video.videoHeight;
        const { deviceInfo, canvas, facingMode } = this._props;
        try{
            (() => {
                if(canvas.isConnected || !deviceInfo){
                    return;
                }
                if(!deviceInfo.isIOS || (deviceInfo.safariVersion && deviceInfo.safariVersion >= 16)){
                    return;
                }
                let cvsLayer = (() => {
                    if(!this._props.cvsLayer){
                        const cvsLayer = document.createElement('div');
                        Object.assign(cvsLayer.style, {
                            position: 'absolute',
                            left: 0, top: 0,
                            opacity: 0,
                            // width: '5px', height: '5px',
                            transform: 'scale(0.1)',
                            pointerEvents: 'none',
                            overflow: 'hidden'
                        });
                        document.body.append(cvsLayer);
                        this._props.cvsLayer = cvsLayer;
                    }
                    return this._props.cvsLayer;
                })();
                cvsLayer.append(canvas);
            })();
        }catch(e){}

        let ctx = this._getRenderContext();

        let sX=0, sY=0, ratio=1;
        let {width, height} = canvas;
        if(canvas._ratio !== videoFrameRatio){
            // 비율이 달라지면 이전 잔상이 남아 clear 해준다.
            ctx.clearRect(0,0, width, height);
        }
        canvas._ratio = videoFrameRatio;

        let dX=0, dY=0;
        let {videoWidth, videoHeight} = video;

        (() => {
            // ios 최신 기기에서 가로모드로 일정 시간 경과하면 전면카메라가 portrait 로 강제 전환되는 현상.
            if(!this._props.deviceInfo.isIOS) return;
            const orientation = globalThis.screen?.orientation;
            if(facingMode !== 'user' || !orientation?.type.includes('landscape')){
                return;
            }
            const {track} = this._props;
            if(!track) return;
            const settings = track.getSettings();
            const cons = track.getConstraints();
            if(cons.width !== settings.height || cons.height !== settings.width){
                return;
            }

            const flipCtx = (() => {
                let {flipCtx} = this._props;
                if(!flipCtx){
                    const cvs = createOffscreenCanvas(videoHeight, videoWidth);
                    this._props.flipCtx = cvs.getContext('2d');
                    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
                }else{
                    const {width, height} = flipCtx.canvas;
                    if(width !== videoHeight || height !== videoWidth){
                        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
                        Object.assign(flipCtx.canvas, {
                            width: videoHeight, height: videoWidth
                        });
                    }
                }
                return this._props.flipCtx;
            })();

            try{
                flipCtx.save();
                let draw = false;
                let flipAngle = orientation?.angle || 90;
                switch(flipAngle){
                    case -90:
                        draw = true;
                        flipCtx.setTransform(0, -1, 1, 0, 0, flipCtx.canvas.height);
                        break;
                    case 90:
                        draw = true;
                        flipCtx.setTransform(0, 1, -1, 0, flipCtx.canvas.width, 0);
                        //flipCtx.setTransform(0, 1, -1, 0, -flipCtx.canvas.height, flipCtx.canvas.width);
                        break;
                }
                if(draw){
                    flipCtx.clearRect(0, 0, flipCtx.canvas.width, flipCtx.canvas.height);
                    flipCtx.drawImage(video, 0, 0, videoWidth, videoHeight);
                    video = flipCtx.canvas;
                    videoWidth = flipCtx.canvas.width;
                    videoHeight = flipCtx.canvas.height;
                }
            }finally{
                flipCtx.restore();
            }
        })();

        if(width !== videoWidth || height !== videoHeight){
            const {resizeMode} = this._props;
            switch(resizeMode){
                case 'crop': {
                    if(videoWidth > width || videoHeight > height){
                        let wRatio = width / videoWidth;
                        let hRatio = height / videoHeight;
                        ratio = Math.max(wRatio, hRatio);
                        sX = ((videoWidth * ratio) - width) / 2 / ratio;
                        sY = ((videoHeight * ratio) - height) / 2 / ratio;
                        videoWidth = width / ratio;
                        videoHeight = height / ratio;
                        break;
                    }
                }
                case 'res':
                default:
                    let wRatio = width / videoWidth;
                    let hRatio = height / videoHeight;
                    ratio = Math.min(wRatio, hRatio)
                    width = videoWidth * ratio;
                    height = videoHeight * ratio;
                    dX = (canvas.width - width) / 2;
                    dY = (canvas.height - height) / 2;
                    break;
            }
        }

        const {mirror, flipAngle} = this._props;
        const setTranslate = (ctx) => {
            const trans = mirror || flipAngle;
            try{
                (() => {
                    if(!trans) return;
                    ctx.save();
                    if(mirror){
                        // ctx.scale(-1, 1);
                        // ctx.translate(-ctx.canvas.width, 0);
                        ctx.setTransform(-1, 0, 0, 1, ctx.canvas.width, 0);
                    }
                    // if(flipAngle){
                    //     switch(flipAngle){
                    //         case -90:
                    //             ctx.setTransform(0, -1, 1, 0, 0, ctx.canvas.width);
                    //             break;
                    //         case 90:
                    //             ctx.setTransform(0, 1, -1, 0, -ctx.canvas.height, ctx.canvas.width);
                    //             break;
                    //     }
                    // }
                })();
                ctx.drawImage(video,
                    sX, sY, videoWidth, videoHeight,
                    dX, dY, width, height
                );
            }finally{
                if(trans){
                    ctx.restore();
                }
            }
        };
        if(this.pipes.length === 0){
            setTranslate(ctx);
        }else{
            let {
                srcCtx, srcCanvas,
                destCtx, destCanvas
            } = this._props;
            if(!srcCtx){
                srcCanvas = document.createElement('canvas');
                srcCtx = srcCanvas.getContext('2d');
                destCanvas = document.createElement('canvas');
                destCtx = destCanvas.getContext('2d');
                Object.assign(this._props, {
                    srcCtx, srcCanvas,
                    destCtx, destCanvas
                });
            }
            Object.assign(srcCanvas, {
                width, height
            });
            Object.assign(destCanvas, {
                width, height
            });

            setTranslate(srcCtx);
            destCtx.drawImage(srcCanvas, 0, 0, width, height);
            const jobs = this.pipes.map(pipe => {
                return pipe.instance.render({
                    image: srcCanvas,
                    canvas: destCanvas,
                    ctx: destCtx
                })
            });
            await Promise.all(jobs);
            ctx.drawImage(destCanvas, 0, 0, ctx.canvas.width, ctx.canvas.height);
        }

        // event trigger
        this._triggerVideoFrameEvent({canvas})
    }

    requestVideoFrameRender (fps) {
        if(this.paused()){
            return;
        }
        this._paused = false;

        const video = this.getSourceVideo();
        if(video.paused){
            video.play();
        }
        if(!video._pause_listener){
            video._pause_listener = e => {
                if(this.paused()) return;
                if(document.visibilityState === 'visible'){
                    video.play();
                    raf();
                    return;
                }
                console.log('video paused. document.visibilityState:', document.visibilityState);
                let setListener = () => {
                    document.addEventListener('visibilitychange', e => {
                        if(document.visibilityState !== 'visible'){
                            setListener();
                            return;
                        }
                        if(this.paused()) return;
                        console.log('video play. document.visibilityState:', document.visibilityState);
                        video.play();
                        raf();
                    }, {once: true});
                }
                setListener();
            }
            video.addEventListener('pause', video._pause_listener);
        }

        const raf = () => {
            const task = () => {
                delete this._fps_a_timer;
                delete this._fps_v_timer;
                if(this.paused()) return;
                this.render();
                this.requestVideoFrameRender(fps);
            }
            // if(document.visibilityState === 'hidden'){
            //     console.log('@@@@@@@@@@@@ hidden:', new Date().toLocaleString())
            //     queueMicrotask(task);
            // }else{
            //     if(video.requestVideoFrameCallback) {
            //         this._fps_v_timer && video.cancelVideoFrameCallback(this._fps_v_timer);
            //         this._fps_v_timer = video.requestVideoFrameCallback(task);
            //     }else{
            //         this._fps_a_timer && cancelAnimationFrame(this._fps_a_timer);
            //         this._fps_a_timer = requestAnimationFrame(task);
            //     }
            // }

            // if(document.visibilityState === 'hidden') {
            //     console.log('@@@@@@@@@@@@ hidden:', new Date().toLocaleString())
            // }
            queueMicrotask(task);
        }

        let trackFrame = (() => {
            if(video.srcObject){
                return video.srcObject.getVideoTracks()[0]?.getSettings().frameRate || 30;
            }
            if(video.getVideoPlaybackQuality){
                if(Date.now() - (video._q_chk_time||0) > 5000){
                    const oq = video._old_q;
                    const q = video.getVideoPlaybackQuality();
                    video._old_q = q;
                    if(oq){
                        video._q_chk_time = Date.now();
                        let time = q.creationTime - oq.creationTime;
                        let frames = q.totalVideoFrames - oq.totalVideoFrames;
                        let f = frames * (1000 / time);
                        return Math.round(f);
                    }
                }
            }
            return 30;
        })()
        let frame = fps || this._fps || 30;
        // if(frame === trackFrame){
        //     raf();
        // }else{
            if(this._fps_timer){
                clearTimeout(this._fps_timer);
            }
            this._fps_timer = setTimeout(() => {
                delete this._fps_timer;
                raf();
            }, 1000 / (Math.min(frame, trackFrame)));
        // }
    }

    setBlur (blurSize) {
        const set = blurSize !== false && blurSize > 0;
        let ctx = this._getRenderContext();
        if(set){
            ctx.filter = `blur(${blurSize}px)`
        }else{
            ctx.filter = ``
        }
    }

    setMirror (mirror) {
        this._props.mirror = mirror !== false;
    }
    setFlip (angle) {
        // angle: -90, 90
        if(angle === false){
            this._props.flipAngle = null;
            return;
        }
        if(Math.abs(angle) !== 90) return;
        this._props.flipAngle = angle;
    }

    changeFPS (fps) {
        if(isNaN(fps)){
            return;
        }
        this._fps = parseFloat(fps);
    }

    paused () {
        return this._paused === true;
    }
    pause (options) {
        this._paused = true;
        if(options?.pauseText !== undefined){
            this._props.pauseText = options?.pauseText;
        }
        const {video} = this._props;
        video.pause();
        if(options?.drawPauseImage !== false){
            this._drawPauseImage();
        }
    }
    _drawPauseImage (ctx) {
        if(!this.paused()) return;

        if(!ctx){
            ctx = this._props.ctx;
            if(!ctx){
                ctx = this._props.ctx = this._props.canvas.getContext('2d');
            }
        }
        let canvas = ctx.canvas;
        let {pauseImage, pauseText} = this._props;
        pauseText = pauseText || '● ● ●';
        if(!pauseImage
            || pauseText !== pauseImage.pauseText
            || this.destroyed
        ){
            const ocs = createOffscreenCanvas(canvas.width, canvas.height);
            const ocsCtx = ocs.getContext('2d');
            ocsCtx.fillStyle = '#000';
            ocsCtx.fillRect(0, 0, ocs.width, ocs.height);
            const fontSize = canvas.height * 0.08;
            Object.assign(ocsCtx, {
                font: `${fontSize}px Arial`,
                textAlign: 'center',
                textBaseline: 'middle',
                fillStyle: '#bdbdbd'
            });
            if(!this.destroyed){
                ocsCtx.fillText(pauseText, ocs.width / 2, ocs.height / 2);
            }
            pauseImage = this._props.pauseImage = ocs;
            pauseImage.pauseText = pauseText;
        }
        if(ctx){
            ctx.drawImage(pauseImage,
                0, 0, canvas.width, canvas.height
            );
        }
    }

    resume () {
        this._paused = false;
        this.requestVideoFrameRender();
    }

    resize (width, height, resizeMode) {
        this.resizeCanvas(width, height);
        this._props.resizeMode = resizeMode;
    }

    resizeCanvas (width, height) {
        const canvas = this.getRenderCanvas();
        Object.assign(canvas, {width, height});
    }

    async toBlob (type, quality, callback) {
        return new Promise((resolve, reject) => {
            const { canvas } = this._props;
            try{
                canvas.toBlob(result => {
                    if(callback){
                        callback(result);
                    }
                    resolve(result);
                }, type, quality);
            }catch(e){
                reject(e);
                if(callback){
                    callback(null, e);
                }
            }
        });
    }

    toDataURL (type, encoderOptions) {
        const { canvas } = this._props;
        return canvas.toDataURL(type, encoderOptions);
    }

    getSourceMediaStream () {
        const stream = this.getSourceVideo().srcObject;
        return stream;
    }

    getSourceVideoMediaStreamTrack () {
        const stream = this.getSourceMediaStream();
        if(!stream) return null;
        return stream.getVideoTracks()[0];
    }

    getMediaStream (checkSet) {
        if(this._stream && checkSet !== true){
            return this._stream;
        }
        const canvas = this.getRenderCanvas();
        let stream;
        if(checkSet === true && this._stream){
            stream = this._stream
        }else{
            stream = this._stream = canvas.captureStream();
        }

        const track = stream.getVideoTracks()[0];
        track._grm_tg = this;
        // const oriTrack = this.getSourceVideoMediaStreamTrack();

        // modify oriTrack
        (() => {
            const oriTrack = this.getSourceVideoMediaStreamTrack();
            if(oriTrack._grm_vtp) return;
            oriTrack._grm_tg = this;
            oriTrack._grm_vtp = {
                getSenderTrack: () => {
                    return this.getVideoMediaStreamTrack();
                },
                oriApplyConstraints: ((t, func)=>{
                    let options = this._options;
                    return function(){
                        // if(options.maintainRatio === true){
                        //     let {width, height} = t.getSettings();
                        //     Object.assign(arguments[0], {width, height});
                        // }
                        if(options.maintainRatio === true) return Promise.resolve();
                        return func.apply(t, arguments);
                    }
                })(oriTrack, track.applyConstraints),
            };
            oriTrack.applyConstraints = async constraints => {
                // 아래 로직 확인 필요
                // return oriTrack._grm_vtp.oriApplyConstraints(constraints);
                const track = this.getVideoMediaStreamTrack();
                return track.applyConstraints(constraints);
            };
        })();
        // modify capture track
        const modifyCaptureTrack = (track) => {
            if(track._grm_vtp) return;
            track._grm_tg = this;
            track._grm_vtp = {
                getOriTrack: () => {
                    const track = this.getSourceVideoMediaStreamTrack();
                    return track;
                },
                oriApplyConstraints: ((t, func)=>{
                    let options = this._options;
                    return function(){
                        // if(options.maintainRatio === true){
                        //     let {width, height} = t.getSettings();
                        //     Object.assign(arguments[0], {width, height});
                        // }
                        if(options.maintainRatio === true) return Promise.resolve();
                        return func.apply(t, arguments);
                    }
                })(track, track.applyConstraints),
                jobs: []
            }
            const applyConstraints = async constraints => {
                await track._grm_vtp.oriApplyConstraints(constraints);
                const settings = track.getSettings();
                let change = true;
                for(let key in constraints){
                    if(constraints[key] != settings[key]){
                        change = false;
                        break;
                    }
                }

                if(constraints.width && constraints.height){
                    this.resize(constraints.width, constraints.height, 'fit');
                }
                if(!change){
                    if(constraints.width && constraints.height){
                        if(constraints.width > canvas.width || constraints.height > canvas.height){
                            const oriTrack = this.getSourceVideoMediaStreamTrack();
                            const {width, height} = constraints;
                            const cons = oriTrack.getConstraints();
                            Object.assign(cons, {width, height});
                            await oriTrack._grm_vtp.oriApplyConstraints(cons);
                        }
                    }
                    if(constraints.frameRate){
                        this._stream = canvas.captureStream(constraints.frameRate);
                        const newTrack = this._stream.getVideoTracks()[0];
                        stream.addTrack(newTrack);
                        stream.removeTrack(track);
                        track.stop();
                        modifyCaptureTrack(newTrack);
                    }
                }
            };
            track.applyConstraints = async constraints => {
                const jobs = track._grm_vtp.jobs;
                if(jobs.runJob){
                    // console.log('@@ track apply cons : running, jobs:', jobs.length+1, Date.now());
                    let job = {};
                    job.p = new Promise((resolve, reject) => {
                        Object.assign(job, {
                            resolve, reject, constraints,
                            created: Date.now()
                        });
                    });
                    jobs.push(job);
                    return job.p;
                }

                jobs.runJob = true;
                // console.log('@@@@@@@ track apply cons : run, jobs:', jobs.length, constraints);

                try{
                    await applyConstraints.call(track, constraints);
                }finally{
                    // console.log('@@ finally:', constraints)
                    jobs.runJob = false;
                    if(track._grm_vtp.jobs.length > 0){
                        const job = track._grm_vtp.jobs.shift();
                        // console.log('@@ track apply cons : run next, jobs:',
                        //     jobs.length+1, job.constraints, (Date.now()-job.created)+'ms', Date.now());
                        track.applyConstraints(job.constraints)
                            .then(job.resolve)
                            .catch(job.reject);
                    }
                }
            };
        }
        modifyCaptureTrack(track);

        return this._stream;
    }

    getVideoMediaStreamTrack () {
        const stream = this.getMediaStream();
        return stream.getVideoTracks()[0];
    }

    replaceVideoMediaStreamTrack (track) {
        const stream = this.getSourceMediaStream();
        const old = stream.getVideoTracks()[0];
        if(track && track === old) return;
        if(track){
            stream.addTrack(track);
        }
        if(old){
            stream.removeTrack(old);
        }

        const {facingMode} = track.getSettings();
        this._props.facingMode = facingMode;

        this.getMediaStream(true);
    }

    async applyVideoMediaStreamTrackConstraints (constraints) {
        const track = this.getVideoMediaStreamTrack();
        const cons = track.getConstraints();
        Object.assign(cons, constraints);
        return track.applyConstraints(cons);
    }

    async pipeThrough(name, options) {
        // console.log('@@ add pipe:', name, options);
        const pipe = this.pipes.find(pipe => pipe.name === name);
        if(pipe){
            this.setPipeOptions(name, options);
            return;
        }

        const Pipe = GrmMediaPipeObject.get(name);
        if(!Pipe){
            console.warn('not supported');
            return;
        }
        const instance = new Pipe(options);
        await instance.init();

        const pipeObj = {
            name,
            instance
        };
        this.pipes.push(pipeObj);
    }
    async setPipeOptions(name, options) {
        if(!options) return;
        const pipe = this.pipes.find(pipe => pipe.name === name);
        if(!pipe) return;
        pipe.instance.setOptions(options);
    }

    // Face Detection 인터페이스 메서드들
    async setFaceImage(faceImage) {
        console.log('🔍 GrmMediaStreamTrackCanvas.setFaceImage called');
        const faceDetectorPipe = this.pipes.find(pipe => pipe.name === 'FaceDetector');
        if (faceDetectorPipe && faceDetectorPipe.instance) {
            console.log('🔍 Found FaceDetector pipe, delegating setFaceImage');
            if (typeof faceDetectorPipe.instance.setFaceImage === 'function') {
                return await faceDetectorPipe.instance.setFaceImage(faceImage);
            } else {
                console.warn('⚠️ FaceDetector instance does not have setFaceImage method');
            }
        } else {
            console.warn('⚠️ FaceDetector pipe not found');
        }
    }

    setFaceDrawType(drawType) {
        console.log('🔍 GrmMediaStreamTrackCanvas.setFaceDrawType called with:', drawType);
        const faceDetectorPipe = this.pipes.find(pipe => pipe.name === 'FaceDetector');
        if (faceDetectorPipe && faceDetectorPipe.instance) {
            console.log('🔍 Found FaceDetector pipe, delegating setFaceDrawType');
            if (typeof faceDetectorPipe.instance.setFaceDrawType === 'function') {
                return faceDetectorPipe.instance.setFaceDrawType(drawType);
            } else {
                console.warn('⚠️ FaceDetector instance does not have setFaceDrawType method');
            }
        } else {
            console.warn('⚠️ FaceDetector pipe not found');
        }
    }
}